//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers, ZeroAddress } from 'ethers'

export type EthAddr = string
export type EthWallet = string
export type TxHash = `0x${string}`

export const ETH_ADDR_ZERO = ZeroAddress as EthAddr
export type ProviderWritable = ethers.BrowserProvider | ethers.JsonRpcProvider
export type ProviderReadOnly = ethers.JsonRpcProvider
export type ProviderWebSocket = ethers.WebSocketProvider

export const dateToEthDate = (date: Date): number => {
  return Math.round(date.getTime() / 1000)
}
export const stringToEthDate = (dateStr: string): number => {
  const [year, month, day] = dateStr.split('-')
  const timestamp = Date.UTC(Number(year), Number(month) - 1, Number(day))
  return Math.round(timestamp / 1000)
}
export const ethDateToString = (ethDate: number): string => {
  const d = new Date(ethDate * 1000)
  let month = `${d.getMonth() + 1}`
  let day = `${d.getDate()}`
  const year = `${d.getFullYear()}`

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  const dateString = [year, month, day].join('-')
  return dateString
}
export enum NumberFormatEnum {
  EXCHANGE_RATE = 'EXCHANGE_RATE',
  EXCHANGE_RATE_LONG = 'EXCHANGE_RATE_LONG',
  APR = 'APR',
  ERC20 = 'ERC20',
}
export enum StringTypeEnum {
  ID = 'ID',
  STRING = 'STRING',
  UINT32 = 'UINT32',
  STABLE_COINS = 'STABLE_COINS',
  TIME_INTERVAL_SECONDS = 'TIME_INTERVAL_SECONDS',
  TIME_INTERVAL_HOURS = 'TIME_INTERVAL_HOURS',
  TIME_INTERVAL_DAYS = 'TIME_INTERVAL_DAYS',
  BPS = 'BPS',
  ETH_ADDR = 'ETH_ADDR',
  PPT_SHARES = 'PPT_SHARES',
  APR = 'APR',
  SINGLE_DAY_APR = 'SINGLE_DAY_APR',
  ETH_DATE = 'ETH_DATE',
  ETH_TIME_OF_DAY = 'ETH_TIME_OF_DAY',
  ETH_DAY_NUMBER = 'ETH_DAY_NUMBER',
  ETH_TIME = 'ETH_TIME',
  ETH_DATE_TIME = 'ETH_DATE_TIME',
  ETH_DATE_TIME_STRING = 'ETH_DATE_TIME_STRING',
  EXCHANGE_RATE = 'EXCHANGE_RATE',
  EXCHANGE_RATE_LONG = 'EXCHANGE_RATE_LONG',
  TX_HASH = 'TX_HASH',

  SYMBOL = 'SYMBOL',
  DECIMAL_STRING = 'DECIMAL_STRING',
  UNIX_DATE = 'UNIX_DATE',
  OFFCHAIN_RATE = 'OFFCHAIN_RATE',
  STRING_ENUM = 'STRING_ENUM',
  MAP_REDUCE = 'MAP_REDUCE',
  STABLE_COIN_DEFINITION = 'STABLE_COIN_DEFINITION',
  DATA_VERSION = 'DATA_VERSION',
  DEF_TYPE_ENUM = 'DEF_TYPE_ENUM',
}

export const ArrayTypeEnum = {
  POOL_ACCESS_CONTROL: 'POOL_ACCESS_CONTROL',
  VAULT_EVENT: 'VAULT_EVENT',
  PORTFOLIO_POOL: 'PORTFOLIO_POOL',
  FEE_ACCRUAL_ACCOUNT: 'FEE_ACCRUAL_ACCOUNT',
}

export enum CTOReducerEnum {
  AVERAGE = 'AVERAGE',
  SUM = 'SUM',
  MAX = 'MAX',
  FIRST = 'FIRST',
  LAST = 'LAST',
}

export const ethExchangeRateToDisplay = (ethExchange: bigint): bigint => {
  return ethExchange / BigInt(1e12)
}

export interface OtObjectMetaData {
  tableName: string
  rls: boolean
}

export interface OtFieldMetaDataInput {
  propName: string
  stringType?: StringTypeEnum
  columnVisibility?: ReportingColumnVisibilityEnum
  columnOrder?: number
  columnWidth?: number
  choices?: string[]
  description?: string
  example?: string | number

  offChain?: boolean
  stateToString?: Record<number, string>
  enumValues?: string[]
  map?: string
  reducer?: CTOReducerEnum
  objectType?: string
  arrayType?: string
}

export enum ReportingColumnVisibilityEnum {
  DefaultVisible = 'DefaultVisible',
  DefaultHidden = 'DefaultHidden',
  Disabled = 'Disabled',
}
export interface FieldMetaData extends Record<string, unknown> {
  propName: string
  stringType: StringTypeEnum
  columnVisibility?: ReportingColumnVisibilityEnum
  columnOrder?: number
  columnWidth?: number
  description?: string
  example?: string | number
  choices?: string[]

  defaultDisplayValue?: string
  liquidityTokenSymbol?: string
  map?: string
  reducer?: CTOReducerEnum
  columnVisible?: boolean
  objectType?: string
}

export function passObjectMetaData(objectMetaData: OtObjectMetaData): Record<string, unknown> {
  return objectMetaData as unknown as Record<string, unknown>
}
export function toFieldMetaData(metaIn: OtFieldMetaDataInput): FieldMetaData {
  const fieldMetaData: FieldMetaData = {
    ...metaIn,
    arrayType: metaIn.arrayType,
    stringType: metaIn.stringType ?? StringTypeEnum.STRING,
    description: metaIn.description ?? '',
    example: metaIn.example ?? '',
    map: metaIn.map ?? '',
    reducer: metaIn.reducer,
    columnVisibility: metaIn.columnVisibility ?? ReportingColumnVisibilityEnum.DefaultHidden,
    columnOrder: metaIn.columnOrder ?? Number.MAX_SAFE_INTEGER,
    columnWidth: metaIn.columnWidth,
  }
  return fieldMetaData
}

export type PoolAccountAccessPair = {
  accountAddr: EthAddr
  poolAddr: EthAddr
}
